import { Link } from '@/components';
import { Card, CardProps, LinkWrapper } from '@blockanalitica/ui';
import type { LinkWrapperProps } from '@blockanalitica/ui/package/types/src/components/atoms/LinkWrapper';
import { ReactNode } from 'react';
import { To } from 'react-router';
import styled, { css } from 'styled-components';

interface LinkCardProps extends Omit<CardProps, 'title'> {
  to?: To;
  href?: string;
  target?: string;
  rel?: string;
  title?: string | ReactNode;
  action?: ReactNode;
  hoverBorderColor?: string;
  children: ReactNode | ReactNode[];
}

interface LinkCardWrapperProps extends LinkWrapperProps {
  $hoverBorderColor: string;
}

const LinkCardContent = styled(Card)``;
const LinkCardWrapper = styled(LinkWrapper)<LinkCardWrapperProps>`
  display: flex;
  flex: 1;
  flex-direction: column;
  color: unset;
  text-decoration: unset;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    color: unset;
    text-decoration: unset;
  }

  ${(props) => css`
    & ${LinkCardContent} {
      outline: solid 1px ${props.theme.colors.onSurface};
      outline-offset: -1px;
    }

    &:hover ${LinkCardContent} {
      outline-color: ${props.$hoverBorderColor.startsWith('#')
        ? props.$hoverBorderColor
        : props.theme.colors[props.$hoverBorderColor]};
    }
  `}
`;

export default function LinkCard({
  to,
  href,
  children,
  title,
  action,
  hoverBorderColor = 'linkHover',
  target,
  rel,
  ...rest
}: LinkCardProps) {
  const isExternal = Boolean(href);

  return isExternal ? (
    <LinkCardWrapper
      href={href}
      target={target}
      rel={rel}
      $hoverBorderColor={hoverBorderColor}>
      <LinkCardContent {...rest}>{children}</LinkCardContent>
    </LinkCardWrapper>
  ) : (
    <LinkCardWrapper
      to={to as To}
      $hoverBorderColor={hoverBorderColor}
      as={Link}>
      <LinkCardContent {...rest}>{children}</LinkCardContent>
    </LinkCardWrapper>
  );
}
